'use client';

import { logger } from '@adornis/base/logging.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import { Loader2, Paperclip, Send } from 'lucide-react';
import { type DragEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPendingFileMutation, deletePendingFileMutation } from '../../../db/api/pending-file.js';
import OwlLogo from '../../assets/OwlTransparent.svg';
import { useCurrentUser } from '../../lib/useCurrentUser.js';
import { useCurrentUserProfile } from '../../lib/useCurrentUserProfile.js';
import { StudentDashboardWidgets } from './student-dashboard-widgets.js';
import { TeacherDashboardWidgets } from './teacher-dashboard-widgets.js';

interface DashboardViewProps {
  userName?: string;
  onSubmit: (text: string, opts?: { attachedFileNames?: string[] | null }) => void;
  examId?: string | null;
  onEnsureExam?: () => Promise<string | null>;
}

export function DashboardView({ onSubmit, examId, onEnsureExam }: DashboardViewProps) {
  const { t } = useTranslation();

  const [input, setInput] = useState('');
  const [greeting, setGreeting] = useState('');
  const [attachedPendingFiles, setAttachedPendingFiles] = useState<
    Array<{ name: string; pendingId: string; examId: string }>
  >([]);
  const [uploadingFiles, setUploadingFiles] = useState<Array<{ id: string; name: string }>>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { user } = useCurrentUser();
  const { role, teacher, student } = useCurrentUserProfile();

  const isGuest = !user;
  const profileFirstName = role === 'student' ? student?.firstName : teacher?.firstName;
  const userName = profileFirstName || user?.username || '';

  const sendMessage = () => {
    if (isUploadingFile) return;
    const trimmedInput = input.trim();
    const attachedFileNames = attachedPendingFiles.map(file => `${file.pendingId}:${encodeURIComponent(file.name)}`);
    if (!trimmedInput && attachedFileNames.length === 0) return;

    onSubmit(trimmedInput, {
      attachedFileNames,
    });
    setInput('');
    setAttachedPendingFiles([]);
  };

  const handleFileSelect = async (files: FileList | null) => {
    const selectedFiles = files ? Array.from(files) : [];
    if (selectedFiles.length === 0) return;

    const filesToUpload = selectedFiles.map((file, index) => ({
      file,
      tempId: `${file.name}-${Date.now()}-${index}`,
    }));

    setIsUploadingFile(true);
    setUploadingFiles(filesToUpload.map(({ tempId, file }) => ({ id: tempId, name: file.name })));

    try {
      let targetExamId = examId ?? null;
      if (!targetExamId && onEnsureExam) {
        targetExamId = await onEnsureExam();
      }

      if (!targetExamId) {
        throw new Error(t('Dashboard.alerts.noExamId'));
      }

      for (const { file, tempId } of filesToUpload) {
        let uploadedIds: string[] = [];
        if (file.type === 'application/pdf') {
          const pdfId = await PDFFile.upload(file);
          uploadedIds = [pdfId];
        } else {
          const imgId = await ImageFile.upload(file);
          uploadedIds = [imgId];
        }

        const pendingId = await createPendingFileMutation(targetExamId, uploadedIds, file.name, file.type, file.size)();

        setAttachedPendingFiles(prev => [
          ...prev,
          { name: file.name, pendingId: String(pendingId), examId: targetExamId },
        ]);
        setUploadingFiles(prev => prev.filter(item => item.id !== tempId));
      }
    } catch (err) {
      logger.error(err, `Upload failed`);
      alert('Fehler beim Hochladen der Datei');
    } finally {
      setIsUploadingFile(false);
      setUploadingFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const isFileDrag = (event: DragEvent<HTMLElement>) => Array.from(event.dataTransfer?.types ?? []).includes('Files');

  const resetDragState = () => {
    dragCounterRef.current = 0;
    setIsDragOver(false);
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    const droppedFiles = event.dataTransfer.files;
    resetDragState();
    void handleFileSelect(droppedFiles);
  };

  useEffect(() => {
    if (isGuest) {
      setGreeting(t('Dashboard.guestMessage'));
    } else {
      const greetingsKey = role === 'student' ? 'Dashboard.studentGreetings' : 'Dashboard.greetings';
      const greetingsObj = t(greetingsKey, { returnObjects: true });

      const greetingsArray =
        typeof greetingsObj === 'object' && greetingsObj !== null ? Object.values(greetingsObj) : [];

      if (greetingsArray.length > 0) {
        const randomIndex = Math.floor(Math.random() * greetingsArray.length);
        setGreeting(greetingsArray[randomIndex] as string);
      } else {
        setGreeting(t('Dashboard.fallbackGreeting'));
      }
    }
  }, [isGuest, role, t]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() || attachedPendingFiles.length > 0) {
        sendMessage();
      }
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full w-full max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-0">
      {/* Greeting Section */}
      <div className="w-full max-w-3xl mb-10 space-y-1">
        <div className="flex items-center gap-2 text-lg font-medium text-muted-foreground">
          <img src={OwlLogo} alt="Owl Logo" className="h-10 w-10 object-contain" />
          <span>
            {t('Dashboard.greetingPrefix')}
            {isGuest ? '' : ` ${userName}`}
          </span>
        </div>
        <h1 className="text-xl md:text-[2.75rem] leading-tight font-medium text-foreground">{greeting}</h1>
      </div>

      {/* Big Input Card */}
      <div className="w-full max-w-3xl mb-6 sm:mb-8">
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative bg-card rounded-3xl shadow-[0_8px_40px_-12px_rgba(0,0,0,0.1)] border p-2 min-h-[140px] sm:min-h-[220px] flex flex-col transition-shadow hover:shadow-[0_12px_50px_-12px_rgba(0,0,0,0.15)] ${
            isDragOver ? 'border-brand-strong bg-brand-surface ring-2 ring-brand-primary/30' : 'border-border'
          }`}
        >
          {isDragOver && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-3xl border-2 border-dashed border-brand-strong bg-brand-surface/90">
              <span className="text-sm sm:text-base font-medium text-brand-strong">{t('Dashboard.dropzoneText')}</span>
            </div>
          )}
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('Dashboard.inputPlaceholder')}
            className="w-full flex-1 resize-none border-none bg-transparent p-4 sm:p-6 text-base sm:text-lg placeholder:text-muted-foreground text-foreground focus-visible:ring-0 focus-visible:outline-none"
            autoFocus
          />

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={e => {
              void handleFileSelect(e.target.files);
            }}
          />

          {(attachedPendingFiles.length > 0 || uploadingFiles.length > 0) && (
            <div className="px-4 sm:px-6 pb-2 flex items-center gap-2 overflow-x-auto">
              {uploadingFiles.map(file => (
                <div
                  key={file.id}
                  className="flex items-center gap-2 bg-muted border border-border rounded-md px-2 py-1"
                >
                  <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" />
                  <span className="text-xs font-medium text-muted-foreground max-w-[160px] truncate">{file.name}</span>
                </div>
              ))}
              {attachedPendingFiles.map(file => (
                <div
                  key={file.pendingId}
                  className="flex items-center gap-2 bg-background border border-border rounded-md px-2 py-1"
                >
                  <span className="text-xs font-medium text-foreground max-w-[180px] truncate">{file.name}</span>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground text-xs transition-colors"
                    aria-label={t('Dashboard.aria.removeFile')}
                    onClick={async () => {
                      try {
                        await deletePendingFileMutation(file.examId, file.pendingId)();
                      } catch (err) {
                        logger.error(err, `Error deleting pending file`);
                      }
                      setAttachedPendingFiles(prev => prev.filter(item => item.pendingId !== file.pendingId));
                    }}
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between items-center gap-2 sm:gap-3 px-3 sm:px-4 pb-3 sm:pb-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-muted-foreground hover:text-foreground h-10 w-10 rounded-full hover:bg-accent flex items-center justify-center transition-colors"
              title={t('Dashboard.aria.uploadFiles')}
            >
              <Paperclip className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={sendMessage}
              disabled={(!input.trim() && attachedPendingFiles.length === 0) || isUploadingFile}
              className="rounded-full h-10 w-10 bg-brand-strong text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              title={t('Dashboard.aria.submit')}
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Action Pills */}
      {role === 'student' ? (
        <StudentDashboardWidgets onSubmit={onSubmit} />
      ) : (
        <TeacherDashboardWidgets onSubmit={onSubmit} />
      )}
    </div>
  );
}
